import {ImageBackground, Pressable, View} from "react-native"
import React, {useMemo} from "react"
import {SchoolClass, SchoolClassCheckIn} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AppHistory from "shared/history"
import BackgroundImage from "images/background.png"
import memo from "set-state-compare/build/memo"
import PageContentsPresentation from "components/page-contents/presentation"
import Params from "@kaspernj/api-maker/build/params"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import Slide from "components/slider/slide"
import Slide1 from "./slide-1"
import Slide2 from "./slide-2"
import Slide3 from "./slide-3"
import Slider from "components/slider"
import {Text} from "shared/base"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import {useFrontLayout} from "components/front-layout"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import useQueryParams from "on-location-changed/build/use-query-params"

/** @typedef {object} RoutesUserInsightsProps */

/**
 * @typedef {object} RoutesUserInsightsState
 * @property {boolean} slide1loaded
 * @property {boolean} slide2loaded
 * @property {boolean} slide3loaded
 */

/**
 * @extends {ShapeComponent<RoutesUserInsightsProps, RoutesUserInsightsState>}
 */
class RoutesUserInsights extends ShapeComponent {
  /** @type {RoutesUserInsightsState} */
  state = {
    slide1loaded: false,
    slide2loaded: false,
    slide3loaded: false
  }

  setup() {
    setLayout("user")

    const {locale, t} = useI18n({namespace: "js.routes.user.insights"})
    const params = useParams()
    const {schoolClass, schoolClassId} = useModel(SchoolClass, {
      match: {params}
    })
    const {schoolClassCheckIn, schoolClassCheckInNotFound} = useModel(
      SchoolClassCheckIn,
      {
        abilities: {
          SchoolClass: ["dataInsights"]
        },
        eventUpdated: true,
        match: {params},
        preload: [
          "school_class.class_step",
          "school_class_course.course",
          "school_class_course.course.result_average_score_factor_group",
          "school_class_course.course.result_bar_chart_score_factor_group",
          "school_class_course.course.result_development_score_factor_group"
        ],
        select: {
          ClassStep: ["primarySchoolStep"],
          Course: [
            "id",
            "resultAverageScoreFactorGroupId",
            "resultBarChartScoreFactorGroupId",
            "resultDevelopmentScoreFactorGroupId",
            "resultPageContentIdentifier"
          ],
          ScoreFactorGroup: ["id", "name"],
          SchoolClass: ["id", "interpretedName", "schoolId"],
          SchoolClassCheckIn: ["completedTeamSurveysCount", "createdAt", "id", "insightsCalculationPending", "state", "updatedAt"],
          SchoolClassCourse: ["courseId", "id", "schoolClassCheckInId", "schoolClassId"]
        }
      }
    )
    const schoolClassCourse = schoolClassCheckIn?.schoolClassCourse()
    const course = schoolClassCourse?.course()
    const averageScoreFactorGroup = course?.resultAverageScoreFactorGroup()
    const averageScoreFactorGroupReady = Boolean(course) && (!course.resultAverageScoreFactorGroupId() || averageScoreFactorGroup)
    const barChartScoreFactorGroup = course?.resultBarChartScoreFactorGroup()
    const barChartScoreFactorGroupReady = Boolean(course) && (!course.resultBarChartScoreFactorGroupId() || barChartScoreFactorGroup)
    const developmentScoreFactorGroup = course?.resultDevelopmentScoreFactorGroup()
    const developmentScoreFactorGroupReady = Boolean(course) && (!course.resultDevelopmentScoreFactorGroupId() || developmentScoreFactorGroup)
    const resultPageContentIdentifier = course?.resultPageContentIdentifier()
    const resultPageLiquidVariables = useMemo(() => {
      if (schoolClass && schoolClassCheckIn && schoolClassCourse && course) {
        return {course, schoolClass, schoolClassCheckIn, schoolClassCourse}
      }

      return null
    }, [course?.id(), schoolClass?.id(), schoolClassCheckIn?.id(), schoolClassCourse?.id()])

    this.averageScoreFactorGroup = averageScoreFactorGroup
    this.averageScoreFactorGroupReady = averageScoreFactorGroupReady
    this.barChartScoreFactorGroup = barChartScoreFactorGroup
    this.barChartScoreFactorGroupReady = barChartScoreFactorGroupReady
    this.currentUser = useCurrentUser()
    this.course = course
    this.developmentScoreFactorGroup = developmentScoreFactorGroup
    this.developmentScoreFactorGroupReady = developmentScoreFactorGroupReady
    this.resultPageContentIdentifier = resultPageContentIdentifier
    this.resultPageLiquidVariables = resultPageLiquidVariables
    this.queryParams = useQueryParams()
    this.schoolClass = schoolClass
    this.schoolClassId = schoolClassId
    this.headerTitle = schoolClassCheckIn && t(".this_is_the_result_of_your_check_in")
    this.schoolClassCheckIn = schoolClassCheckIn
    this.schoolClassCheckInNotFound = schoolClassCheckInNotFound
    this.t = t


    const topLeft = useMemo(
      () => <>
        <Pressable onPress={this.tt.onBackPressed} style={{alignItems: "center", flexDirection: "row"}}>
          <Text style={{marginRight: 10, marginTop: -7, color: "#979797", fontSize: 50}}>
            &lsaquo;
          </Text>
          <Text style={{color: "#5253ff", fontSize: 20, fontWeight: "bold"}}>
            {t(".go_to_the_space_of_the_class")}
          </Text>
        </Pressable>
      </>,
      [locale, schoolClassId]
    )

    useFrontLayout()?.setState({
      headTitle: this.tt.headerTitle,
      logo: false,
      menu: false,
      schoolClass,
      topLeft
    })
  }

  onBackPressed = () => AppHistory.push(Routes.userSchoolClassClassSpacePath(this.tt.schoolClassId))

  render() {
    const {
      averageScoreFactorGroup,
      averageScoreFactorGroupReady,
      barChartScoreFactorGroup,
      barChartScoreFactorGroupReady,
      developmentScoreFactorGroup,
      developmentScoreFactorGroupReady,
      resultPageContentIdentifier,
      resultPageLiquidVariables,
      schoolClass,
      schoolClassCheckIn
    } = this.tt
    const activeSlide = this.activeSlide()
    const refreshVersion = schoolClassCheckIn ? this.insightsRefreshVersion(schoolClassCheckIn) : "no-school-class-check-in"

    if (resultPageContentIdentifier && resultPageLiquidVariables) {
      return (
        <View
          testID="user-school-class-check-in-insights-route"
        >
          <ImageBackground
            resizeMode="cover"
            source={BackgroundImage}
            style={this.resultPageBackgroundStyle ||= {alignItems: "center", minHeight: "100vh", paddingHorizontal: 40, paddingVertical: 80, width: "100%"}}
            testID="school-class-check-in-custom-result-page-background"
          >
            <View style={this.resultPageContentViewStyle ||= {width: "100%", maxWidth: 1000}} testID="school-class-check-in-custom-result-page-content">
              <PageContentsPresentation
                defaultContent={<View testID="school-class-check-in-custom-result-page-empty-content" />}
                identifier={resultPageContentIdentifier}
                liquidVariables={resultPageLiquidVariables}
              />
            </View>
          </ImageBackground>
        </View>
      )
    }

    if (activeSlide == 0) {
      this.setState({slide1loaded: true})
    } else if (activeSlide == 1) {
      this.setState({slide2loaded: true})
    } else if (activeSlide == 2) {
      this.setState({slide3loaded: true})
    }

    return (
      <View
        testID="user-school-class-check-in-insights-route"
      >
        <Slider active={activeSlide} onSlideChanged={this.tt.onSlideChanged} total={3}>
          <Slide active={activeSlide == 0}>
            {this.s.slide1loaded && schoolClass && schoolClassCheckIn && averageScoreFactorGroupReady && barChartScoreFactorGroupReady &&
              <Slide1
                averageScoreFactorGroup={averageScoreFactorGroup}
                barChartScoreFactorGroup={barChartScoreFactorGroup}
                refreshVersion={refreshVersion}
                schoolClass={schoolClass}
                schoolClassCheckIn={schoolClassCheckIn}
              />
            }
          </Slide>
          <Slide active={activeSlide == 1}>
            {this.s.slide2loaded && schoolClass && schoolClassCheckIn && averageScoreFactorGroupReady &&
              <Slide2
                averageScoreFactorGroup={averageScoreFactorGroup}
                insightsCalculationPending={schoolClassCheckIn.insightsCalculationPending()}
                refreshVersion={refreshVersion}
                schoolClass={schoolClass}
                schoolClassCheckIn={schoolClassCheckIn}
              />
            }
          </Slide>
          <Slide active={activeSlide == 2}>
            {this.s.slide3loaded && schoolClass && schoolClassCheckIn && developmentScoreFactorGroupReady &&
              <Slide3
                developmentScoreFactorGroup={developmentScoreFactorGroup}
                refreshVersion={refreshVersion}
                schoolClass={schoolClass}
                schoolClassCheckIn={schoolClassCheckIn}
              />
            }
          </Slide>
        </Slider>
      </View>
    )
  }

  insightsRefreshVersion(schoolClassCheckIn) {
    const completedTeamSurveysCount = schoolClassCheckIn.completedTeamSurveysCount()
    const insightsCalculationPending = schoolClassCheckIn.insightsCalculationPending()
    const updatedAt = schoolClassCheckIn.updatedAt()
    const updatedAtTime = updatedAt ? updatedAt.getTime() : "no-updated-at"

    return `${updatedAtTime}-${completedTeamSurveysCount}-${insightsCalculationPending}`
  }

  activeSlide = () => Number(this.tt.queryParams.slide || 0)
  onSlideChanged = ({number}) => Params.changeParams({slide: number})
}

export default memo(shapeComponent(RoutesUserInsights))
